import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Spinner,
  Text,
  Tooltip,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { FaArrowDown, FaArrowUp, FaCheck, FaCheckCircle, FaFire } from "react-icons/fa";
import { GoDot } from "react-icons/go";
import { fetchController } from "../../../utils/FetchController/fetchController";
import Loader from "../../../utils/Loader/Loader";
import { getHostDomain } from "../../../utils/utils";
import { toast } from "react-toastify";

const isActivated = (v) => {
  if (v === true) return true;
  if (typeof v === "number" && v >= 1) return true;
  if (typeof v === "string" && v.length > 0) return true;
  return false;
};

const fmtNum = (v) => {
  if (v === null || v === undefined || v === 0 || v === false) return null;
  if (v === true) return "Included";
  if (typeof v === "number") return v.toLocaleString();
  return String(v);
};

// Numeric item row — label on left, bold value on right + optional delta arrow.
// Numerics ALWAYS render (with "—" if zero/missing) so rows line up
// across cards; this is the alignment carryover from the synchronized
// version. Boolean items are rendered as chips below the numerics.
function NumericRow({ item, value, prevValue, isFirstTier }) {
  const active = isActivated(value);
  const increased =
    !isFirstTier &&
    typeof value === "number" &&
    typeof prevValue === "number" &&
    value > prevValue;
  const decreased =
    !isFirstTier &&
    typeof value === "number" &&
    typeof prevValue === "number" &&
    value < prevValue;
  const display = fmtNum(value) || "—";

  return (
    <Flex justify="space-between" align="center" py={1}>
      <Text fontSize="13px" color={active ? "gray.700" : "gray.400"}>
        {item.label}
      </Text>
      <HStack spacing={2}>
        <Text
          fontSize="13px"
          fontWeight="bold"
          color={active ? "gray.900" : "gray.400"}
        >
          {display}
        </Text>
        {increased && (
          <Tooltip label={`Up from ${fmtNum(prevValue) || "0"}`}>
            <Badge
              colorScheme="green"
              display="flex"
              alignItems="center"
              fontSize="10px"
            >
              <FaArrowUp size={9} />
            </Badge>
          </Tooltip>
        )}
        {decreased && (
          <Tooltip label={`Down from ${fmtNum(prevValue) || "0"}`}>
            <Badge
              colorScheme="gray"
              display="flex"
              alignItems="center"
              fontSize="10px"
            >
              <FaArrowDown size={9} />
            </Badge>
          </Tooltip>
        )}
      </HStack>
    </Flex>
  );
}

function BooleanChip({ item, isNew }) {
  return (
    <WrapItem>
      <HStack
        spacing={1}
        bg="green.50"
        color="green.700"
        px={2}
        py={1}
        borderRadius="md"
        fontSize="11px"
        fontWeight="medium"
      >
        <FaCheckCircle size={10} />
        <Text>{item.label}</Text>
        {isNew && (
          <Badge colorScheme="purple" fontSize="9px" ml={1}>
            NEW
          </Badge>
        )}
      </HStack>
    </WrapItem>
  );
}

function InactiveChip({ item }) {
  return (
    <WrapItem>
      <Text
        fontSize="11px"
        color="gray.400"
        textDecoration="line-through"
        px={2}
        py={1}
      >
        {item.label}
      </Text>
    </WrapItem>
  );
}

// One group section. Numerics render in every card (anchors alignment).
// Booleans render as chips — only the ones this card has, unless the
// inactive expander has been toggled.
function GroupSection({
  group,
  byKey,
  previousByKey,
  isFirstTier,
  showInactive,
}) {
  const numerics = group.items.filter((i) => i.type === "number");
  const booleans = group.items.filter((i) => i.type === "boolean");
  const activeBooleans = booleans.filter((i) => isActivated(byKey[i.key]));
  const inactiveBooleans = booleans.filter((i) => !isActivated(byKey[i.key]));

  // Skip the group entirely only if there's nothing to render in either
  // column (defensive — shouldn't happen since unionByGroup drops empty
  // groups upstream).
  if (numerics.length === 0 && activeBooleans.length === 0 && (!showInactive || inactiveBooleans.length === 0)) {
    return null;
  }

  return (
    <Box mb={3}>
      <Text
        fontSize="10px"
        textTransform="uppercase"
        color="gray.500"
        fontWeight="bold"
        letterSpacing="0.5px"
        mb={1}
      >
        {group.name}
      </Text>
      {numerics.length > 0 && (
        <Box>
          {numerics.map((item) => (
            <NumericRow
              key={item.key}
              item={item}
              value={byKey[item.key]}
              prevValue={previousByKey?.[item.key]}
              isFirstTier={isFirstTier}
            />
          ))}
        </Box>
      )}
      {(activeBooleans.length > 0 ||
        (showInactive && inactiveBooleans.length > 0)) && (
        <Wrap spacing={1} mt={numerics.length > 0 ? 2 : 0}>
          {activeBooleans.map((item) => (
            <BooleanChip
              key={item.key}
              item={item}
              isNew={
                !isFirstTier && !isActivated(previousByKey?.[item.key])
              }
            />
          ))}
          {showInactive &&
            inactiveBooleans.map((item) => (
              <InactiveChip key={item.key} item={item} />
            ))}
        </Wrap>
      )}
    </Box>
  );
}

const BillingCard = ({
  planName,
  features,
  structuredFeatures,
  unionByGroup,
  previousByKey,
  pricing,
  active,
  popular,
  label,
  erlyBirdsOffer,
  isDisable,
  isCoupan,
  isTrial,
  bg,
  color,
  buttonColorScheme,
  lookup_key,
  customWidth,
}) => {
  const baseUrl = getHostDomain();
  const endpoint = "/api/v1/billing/create-checkout-session";
  const urlHit = baseUrl + endpoint;
  const [loading, setLoading] = useState(false);
  const [isFullScreenLoading, setIsFullScreenLoading] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    return () => setIsFullScreenLoading(false);
  }, []);

  const submit = async (lookup_key, label) => {
    const isConfirmed = window.confirm(
      `Are you sure you want to ${label} plan ?`
    );
    if (!isConfirmed) return;
    setLoading(true);
    try {
      const body = { lookup_key };
      const getData = await fetchController(urlHit, "POST", body);
      if (getData.checkout_url) {
        window.location = getData.checkout_url;
      } else {
        if (getData?.status) {
          toast.success(getData?.message, { autoClose: 4000 });
          setIsFullScreenLoading(true);
          setTimeout(() => {
            window.location.reload();
            setIsFullScreenLoading(false);
          }, 5000);
        } else {
          toast.error(getData?.message, { autoClose: 4000 });
        }
        setLoading(false);
      }
    } catch (error) {
      setIsFullScreenLoading(false);
      console.error("Error occour while upgrading plan: ", error);
    } finally {
      setLoading(false);
    }
  };

  const formatKey = (key) => {
    const words = key ? key.split("_") : [];
    return words
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  };
  const getPlanName = (plan) => {
    if (!plan) return null;
    const nameArr = formatKey(planName).split("-");
    return nameArr[nameArr.length - 1];
  };

  const isFirstTier = !previousByKey;

  const byKey = useMemo(
    () => structuredFeatures?.byKey || {},
    [structuredFeatures]
  );

  // Anchor the group/row skeleton on the union shared by all sibling
  // cards. Fall back to this plan's own groups so the card still works
  // when rendered standalone (no comparison context).
  const groupsForRender = useMemo(
    () =>
      unionByGroup && unionByGroup.length > 0
        ? unionByGroup
        : structuredFeatures?.groups || [],
    [unionByGroup, structuredFeatures]
  );

  // Count inactive booleans (across all groups) for the "+ N more not
  // included" expander. Numerics always render so they're not counted.
  const inactiveCount = useMemo(() => {
    return groupsForRender.reduce((total, g) => {
      const inactiveBools = g.items.filter(
        (i) => i.type === "boolean" && !isActivated(byKey[i.key])
      ).length;
      return total + inactiveBools;
    }, 0);
  }, [groupsForRender, byKey]);

  return (
    <Box
      borderWidth={active ? "3px" : "1px"}
      borderRadius="lg"
      overflow="hidden"
      width={customWidth || "100%"}
      maxW={customWidth || 350}
      boxShadow="lg"
      bg={bg}
      display="flex"
      flexDirection="column"
      borderStyle="solid"
      borderColor={active ? color : "#EAECF0"}
      position="relative"
      h="100%"
      alignSelf="stretch"
    >
      {isFullScreenLoading && (
        <Loader text="We are changing your plan, please wait…" />
      )}

      {/* ─── Header: plan name + price ─── */}
      <Box flexShrink={0}>
        <Flex
          borderBottomColor={active ? color : "#EAECF0"}
          borderBottomWidth={active ? "3px" : "1px"}
          borderStyle="solid"
          bgColor={active && "aliceblue"}
          align="center"
          justify="space-between"
          padding="3px"
        >
          <Text p={2} fontSize="18px" fontWeight="bold">
            {isCoupan ? planName : getPlanName(planName)}
          </Text>
          {popular && (
            <HStack height="25px" padding="3px" bg="#D1E9FF" borderRadius="30px">
              <FaFire color="#F97316" />
              <Text fontSize="12px" fontWeight="bold">
                Popular
              </Text>
            </HStack>
          )}
          {active ? <FaCheck color="#3B82F6" /> : <GoDot size="25px" />}
        </Flex>

        <Box p={2}>
          <Text fontSize="22px" fontWeight="bold">
            {formatKey(pricing)}
          </Text>
          {erlyBirdsOffer && (
            <Text textAlign="center" fontWeight="bold">
              {erlyBirdsOffer}
            </Text>
          )}
        </Box>
      </Box>

      {/* ─── Features region — tile rendering with aligned skeleton ─── */}
      <Box flex="1" p={3}>
        {groupsForRender.length > 0 ? (
          <>
            {groupsForRender.map((g) => (
              <GroupSection
                key={g.name}
                group={g}
                byKey={byKey}
                previousByKey={previousByKey}
                isFirstTier={isFirstTier}
                showInactive={showInactive}
              />
            ))}
            {inactiveCount > 0 && (
              <Box mt={2} textAlign="center">
                <Button
                  variant="ghost"
                  size="xs"
                  fontWeight="normal"
                  color="gray.500"
                  onClick={() => setShowInactive((v) => !v)}
                >
                  {showInactive
                    ? "Hide not included"
                    : `+ ${inactiveCount} more not included`}
                </Button>
              </Box>
            )}
          </>
        ) : (
          // Legacy flat-shape fallback for callers not yet passing
          // structuredFeatures / unionByGroup.
          features?.[0] && (
            <Box>
              {Object.entries(features[0]).map(([lab, value]) => (
                <Flex key={lab} justify="space-between" py={1}>
                  <Text
                    fontSize="13px"
                    color={isActivated(value) ? "gray.700" : "gray.400"}
                  >
                    {lab}
                  </Text>
                  <Text
                    fontSize="13px"
                    fontWeight="bold"
                    color={isActivated(value) ? "gray.900" : "gray.400"}
                  >
                    {fmtNum(value) || "—"}
                  </Text>
                </Flex>
              ))}
            </Box>
          )
        )}
      </Box>

      {/* ─── CTA ─── */}
      <Box flexShrink={0}>
        {!active ? (
          <Tooltip
            label={
              isDisable && isTrial
                ? "You are on a trial period. Plan will automatically be downgraded on its completion."
                : ""
            }
            aria-label={
              isDisable && isTrial
                ? "You are on a trial period. Plan will automatically be downgraded on its completion."
                : ""
            }
          >
            <Button
              onClick={() => submit(lookup_key, label)}
              colorScheme={buttonColorScheme}
              m={3}
              alignSelf="center"
              textTransform="capitalize"
              width="calc(100% - 24px)"
              isDisabled={isDisable}
            >
              {loading ? <Spinner size="xs" /> : label}
            </Button>
          </Tooltip>
        ) : (
          <Button
            colorScheme="blue"
            alignSelf="center"
            width="calc(100% - 24px)"
            m={3}
            disabled
          >
            Activated
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default BillingCard;
